import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { PageHeading } from '@/components/Layout.tsx'
import { LeafDivider, Seedling } from '@/components/Botanical.tsx'
import { Plus } from '@/components/Icons.tsx'
import { api, ApiError } from '@/lib/api.ts'
import { useAuth } from '@/state/AuthContext.tsx'
import type { Member, PromptSummary } from '@/lib/types.ts'

/** The one line that tells you what to do next. */
function state(prompt: PromptSummary, others: Member[]) {
  const theirNames = others.filter((m) => prompt.answeredBy.includes(m.id)).map((m) => m.displayName)
  const waitingOn = others.filter((m) => !prompt.answeredBy.includes(m.id)).map((m) => m.displayName)

  if (prompt.released) return { label: 'Both of you have answered', tone: 'open' as const }
  if (prompt.youAnswered && waitingOn.length > 0)
    return { label: `Written. Waiting for ${waitingOn.join(' and ')}`, tone: 'waiting' as const }
  if (prompt.youAnswered) return { label: 'Written', tone: 'waiting' as const }
  if (theirNames.length > 0)
    return { label: `${theirNames.join(' and ')} answered — your turn`, tone: 'your-turn' as const }
  return { label: 'Neither of you yet', tone: 'idle' as const }
}

const TONE: Record<string, string> = {
  open: 'var(--c-moss)',
  'your-turn': 'var(--c-terracotta)',
  waiting: 'var(--c-gold)',
  idle: 'var(--c-whisper)',
}

export default function Together() {
  const { user } = useAuth()
  const [prompts, setPrompts] = useState<PromptSummary[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [composing, setComposing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.prompts
      .list()
      .then(({ prompts, members }) => { setPrompts(prompts); setMembers(members) })
      .catch(() => setError('These could not be opened.'))
      .finally(() => setLoading(false))
  }, [])

  const others = useMemo(() => members.filter((m) => m.id !== user?.id), [members, user])
  const alone = members.length < 2

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    try {
      const { prompt } = await api.prompts.create(
        String(data.get('text') ?? ''),
        data.get('kind') === 'long' ? 'long' : 'short',
      )
      setPrompts((prev) => [...prev, { ...prompt, answeredBy: [], youAnswered: false, released: false }])
      setComposing(false)
      form.reset()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.')
    }
  }

  return (
    <>
      <PageHeading eyebrow="Answer apart, read together" title="Both of us">
        You each write privately, and neither answer opens until both are in. Not secrecy — it just
        stops whoever goes first from deciding what the other one thinks.
      </PageHeading>

      {alone && !loading && (
        <p className="sheet mb-8 max-w-measure p-5 text-[0.92rem] text-quiet">
          Nothing will open until there are two of you here. Write anyway — it will be waiting.
        </p>
      )}

      {error && <p className="mb-6 text-[0.88rem] text-terracotta">{error}</p>}
      {loading && <p className="text-whisper">Opening…</p>}

      <ul className="divide-y divide-linen border-y border-linen">
        {prompts.map((prompt, i) => {
          const s = state(prompt, others)
          return (
            <li key={prompt.id} className="animate-unfurl" style={{ animationDelay: `${Math.min(i, 10) * 45}ms` }}>
              <Link to={`/together/${prompt.id}`} className="group flex items-start gap-5 py-6">
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full transition-transform duration-300 group-hover:scale-150"
                  style={{ background: TONE[s.tone] }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[1.3rem] leading-[1.35] transition-colors duration-300 group-hover:text-fern">
                    {prompt.text}
                  </span>
                  <span className="mt-2 block text-[0.76rem] tracking-wide" style={{ color: TONE[s.tone] }}>
                    {s.label}
                    {prompt.kind === 'long' && <span className="text-whisper"> · a longer one</span>}
                  </span>
                </span>
                <span className="mt-1 shrink-0 text-sage opacity-0 transition-all duration-300 ease-botanical group-hover:translate-x-1 group-hover:opacity-100">
                  →
                </span>
              </Link>
            </li>
          )
        })}
      </ul>

      <div className="mt-10">
        {composing ? (
          <form onSubmit={add} className="sheet max-w-measure space-y-3.5 p-6 animate-unfurl">
            <label className="block">
              <span className="eyebrow">Ask each other something</span>
              <textarea name="text" required rows={2} autoFocus placeholder="Something you would both have to think about"
                className="field mt-1.5 resize-none" />
            </label>
            <label className="flex items-center gap-2.5 text-[0.85rem] text-quiet">
              <input type="checkbox" name="kind" value="long" className="accent-moss" />
              This one needs room to answer properly
            </label>
            <div className="flex gap-2 pt-1">
              <button type="submit" className="btn-primary">Add it</button>
              <button type="button" onClick={() => setComposing(false)} className="btn-ghost">Never mind</button>
            </div>
          </form>
        ) : (
          <button onClick={() => setComposing(true)} className="btn-ghost">
            <Plus className="h-4 w-4" />
            Ask something of your own
          </button>
        )}
      </div>

      {!loading && prompts.length === 0 && (
        <div className="mt-14 flex flex-col items-center gap-4 text-center">
          <Seedling tone="var(--c-sage)" className="h-16 w-auto opacity-50" />
          <p className="prose-soft max-w-[22rem]">Nothing here yet.</p>
        </div>
      )}

      <LeafDivider tone="var(--c-sage)" className="mx-auto mt-20 h-4 w-40 opacity-30" />
    </>
  )
}
